import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ComponentRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { HSStaticMethods } from 'preline/non-auto';
import { filter } from 'rxjs';
import { CourseHeader } from 'components/course-header/course-header';
import { CourseSidebar } from 'components/course-sidebar/course-sidebar';
import { CourseViewerFooter } from './shared/components/footer/footer';
import { CourseInfo } from 'components/course-info/course-info';
import { CourseViewerService } from 'core/services/course-viewer.service';
import {
  Course,
  CourseNavigation,
  Lesson,
  Module,
} from 'core/models/course-content.model';
import { Video } from './content/video/video';
import { Pdf } from './content/pdf/pdf';
import { Evaluation } from './content/evaluation/evaluation';
import { EvaluationResults } from './content/evaluation-results/evaluation-results';

@Component({
  imports: [
    CommonModule,
    CourseSidebar,
    CourseHeader,
    CourseViewerFooter,
    CourseInfo,
  ],
  standalone: true,
  selector: 'app-root',
  templateUrl: './app.html',
})
export class App implements OnInit, AfterViewInit {
  protected readonly courseService = inject(CourseViewerService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('contentContainer', { read: ViewContainerRef })
  contentContainer!: ViewContainerRef;

  protected title = 'course-viewer';
  protected isSidebarOpen = false;
  protected course: Course | null = null;
  protected navigation: CourseNavigation | null = null;
  protected currentLesson: Lesson | null = null;
  protected currentModule: Module | null = null;
  protected totalProgress = 0;
  protected remainingTime = '~0 min';
  protected lessonProgress = 0;

  private contentComponentRef: ComponentRef<{ lesson?: Lesson }> | null = null;
  private currentLoadedLessonId: string | null = null;
  private isLoadingComponent = false;

  ngOnInit(): void {
    this.isSidebarOpen = this.shouldSidebarStartOpen();

    // Garantir que a primeira lição seja carregada
    const course = this.courseService.getCourse();
    if (course.modules.length > 0) {
      const firstModule = course.modules[0];
      if (
        firstModule.contentType === 'lessons' &&
        firstModule.lessons &&
        firstModule.lessons.length > 0
      ) {
        const firstLessonId = firstModule.lessons[0].id;
        this.courseService.goToLesson(firstLessonId);
      }
    }

    this.loadCourseData();

    // Observar mudanças de lição e recarregar componente quando necessário
    this.courseService.lessonChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lessonId: string) => {
        // Verificar se a lição realmente mudou antes de recarregar
        if (this.currentLoadedLessonId !== lessonId) {
          // Recarregar dados e componente quando a lição mudar
          this.loadCourseData();
        } else {
          // Apenas atualizar dados se for a mesma lição
          this.updateCourseData();
        }
      });
  }

  ngAfterViewInit(): void {
    this.initializePreline();

    // Forçar detecção de mudanças e carregar componente após view estar inicializada
    this.cdr.detectChanges();

    // Carregar componente apenas se ainda não foi carregado e se há uma lição atual
    if (
      !this.contentComponentRef &&
      this.currentLesson &&
      this.contentContainer
    ) {
      this.loadContentComponent();
    }

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.initializePreline());
  }

  protected onToggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
    if (!this.isSidebarOpen) {
      this.releaseSidebarFocus();
    }
  }

  protected onCloseSidebar(): void {
    this.isSidebarOpen = false;
    this.releaseSidebarFocus();
  }

  protected onPreviousLesson(): void {
    if (this.courseService.goToPreviousLesson()) {
      // Não precisa chamar loadCourseData() aqui porque o evento lessonChange$ já vai disparar
      // Apenas atualizar dados imediatamente para melhor responsividade
      this.updateCourseData();
    }
  }

  protected onNextLesson(): void {
    if (this.courseService.goToNextLesson()) {
      // Não precisa chamar loadCourseData() aqui porque o evento lessonChange$ já vai disparar
      // Apenas atualizar dados imediatamente para melhor responsividade
      this.updateCourseData();
    }
  }

  protected onLessonClick(lessonId: string): void {
    // Verificar se já está na mesma lição para evitar recarregamento desnecessário
    if (this.currentLoadedLessonId === lessonId) {
      return;
    }

    this.courseService.goToLesson(lessonId);
    // Não precisa chamar loadCourseData() aqui porque o evento lessonChange$ já vai disparar
    // Apenas atualizar dados imediatamente para melhor responsividade
    this.updateCourseData();
  }

  protected get totalLessonsInModule(): number {
    if (
      !this.currentModule ||
      this.currentModule.contentType !== 'lessons' ||
      !this.currentModule.lessons
    ) {
      return 0;
    }
    return this.currentModule.lessons.length;
  }

  /**
   * Obtém o título a ser exibido no header
   * Para avaliações, mostra o título da lição (texto da questão)
   * Para outras lições, mostra o nome do módulo
   */
  protected get displayTitle(): string {
    if (this.currentLesson && this.currentLesson.type === 'evaluation') {
      return this.currentLesson.title;
    }
    return this.currentModule?.name || '';
  }

  private loadCourseData(): void {
    this.course = this.courseService.getCourse();
    this.navigation = this.courseService.getNavigation();
    this.currentLesson = this.navigation.currentLesson;
    this.currentModule = this.navigation.currentModule;
    this.totalProgress = this.courseService.calculateTotalProgress();
    this.remainingTime = this.courseService.calculateRemainingTime();
    this.lessonProgress = this.courseService.getCurrentLessonProgress();

    // Carregar componente de conteúdo dinamicamente (se view já estiver inicializada)
    if (this.contentContainer && this.currentLesson) {
      this.loadContentComponent();
    }
  }

  private updateCourseData(): void {
    // Atualiza apenas os dados sem recarregar o componente
    // Usado pelo subscription de lessonChange$ para evitar duplicação
    this.course = this.courseService.getCourse();
    this.navigation = this.courseService.getNavigation();
    this.currentLesson = this.navigation.currentLesson;
    this.currentModule = this.navigation.currentModule;
    this.totalProgress = this.courseService.calculateTotalProgress();
    this.remainingTime = this.courseService.calculateRemainingTime();
    this.lessonProgress = this.courseService.getCurrentLessonProgress();
  }

  private async loadContentComponent(): Promise<void> {
    if (!this.currentLesson || !this.contentContainer) {
      return;
    }

    // Evitar duplicação: se já existe um componente para esta lição, não criar novamente
    if (
      this.contentComponentRef &&
      this.currentLoadedLessonId === this.currentLesson.id
    ) {
      return;
    }

    // Evitar carregamento simultâneo
    if (this.isLoadingComponent) {
      return;
    }

    this.isLoadingComponent = true;

    try {
      // Limpar componente anterior antes de criar um novo
      if (this.contentComponentRef) {
        this.contentComponentRef.destroy();
        this.contentComponentRef = null;
        this.currentLoadedLessonId = null;
      }

      // Limpar o container antes de criar novo componente para evitar duplicação
      // Isso garante que não haja componentes órfãos no container
      this.contentContainer.clear();

      // Carregar componente baseado no tipo da lição
      const componentType = await this.getContentComponent(
        this.currentLesson.type,
      );

      if (componentType) {
        this.contentComponentRef =
          this.contentContainer.createComponent(componentType);
        // Atribuir a lição ao componente criado dinamicamente
        // Com OnPush, setInput já marca o componente para verificação automaticamente
        this.contentComponentRef.setInput('lesson', this.currentLesson);
        // Atribuir o módulo ao componente (se o componente aceitar)
        if (
          this.currentModule &&
          'module' in this.contentComponentRef.instance
        ) {
          this.contentComponentRef.setInput('module', this.currentModule);
        }
        // Marcar qual lição está carregada
        this.currentLoadedLessonId = this.currentLesson.id;
        // Marcar para verificação (OnPush requer marcação explícita)
        this.contentComponentRef.changeDetectorRef.markForCheck();
      }
    } finally {
      this.isLoadingComponent = false;
    }
  }

  private async getContentComponent(
    type: Lesson['type'],
  ): Promise<{ new (...args: unknown[]): { lesson?: Lesson } } | null> {
    switch (type) {
      case 'video':
        return Video;
      case 'pdf':
        return Pdf;
      case 'evaluation':
        return Evaluation;
      case 'evaluation-results':
        return EvaluationResults;
      default:
        return null;
    }
  }

  private shouldSidebarStartOpen(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia('(min-width: 1024px)').matches;
  }

  private releaseSidebarFocus(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const activeElement = document.activeElement;
    const sidebarElement = document.getElementById('course-viewer-sidebar');

    if (
      activeElement instanceof HTMLElement &&
      sidebarElement?.contains(activeElement)
    ) {
      activeElement.blur();
    }
  }

  private initializePreline(): void {
    if (typeof window === 'undefined') {
      return;
    }

    requestAnimationFrame(() => {
      HSStaticMethods.cleanCollection();
      HSStaticMethods.autoInit();
      const overlay = (globalThis as { HSOverlay?: { autoInit: () => void } })
        .HSOverlay;
      overlay?.autoInit();
    });
  }
}
